import React from "react";
import { graphql, PreloadedQuery, usePreloadedQuery } from "react-relay";
import { Paper, Typography, styled } from "@mui/material";
import { PredecessorsTabQuery, PredecessorsTabQuery$data } from "./__generated__/PredecessorsTabQuery.graphql";
import MultiColumnTable from "./MultiColumnTable";
import { viewPath } from "../../utils/routeUtils";
import { Link } from "react-router-dom";
import Xarrow from "react-xarrows";

interface PredecessorsTabProps {
  queryRef: PreloadedQuery<PredecessorsTabQuery, Record<string, unknown>>;
}

type PredecessorNode = {
  id: string;
  depth: number;
  typeName: string;
  predecessors: string[];
};

const Container = styled(Paper)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  padding: 20,
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
}));

const cellRenderer = (cellData: object) => {
  const { id, typeName } = cellData as PredecessorNode;
  return (
    <span id={id}>
      <strong>{typeName}</strong>
      <br />
      <Link to={viewPath(id)}>{id}</Link>
    </span>
  );
};

/**
 * Returns true for all types that implement `PredecessorsInterface`
 * @param typeName
 * @returns
 */
export const hasPredecessors = (typeName: string | null | undefined) => {
  switch (typeName) {
    case "File":
    case "AggregateInversionSolution":
    case "InversionSolutionNrml":
    case "InversionSolution":
    case "ScaledInversionSolution":
    case "TimeDependentInversionSolution":
    case "OpenquakeHazardSolution":
      return true;
    default:
      return false;
  }
};

export const indexTree = (data: PredecessorsTabQuery$data): Map<string, PredecessorNode> => {
  if (!data.node?.predecessors) {
    return new Map();
  }

  // index all nodes by their id
  const nodes = data.node.predecessors.reduce((acc, node) => {
    if (node?.id) {
      const metaKeys = node?.node?.meta?.map((kv) => kv?.k ?? "");
      const isRuptureSet = ["fault_model", "scaling_relationship"].every((k) => metaKeys?.includes(k));
      acc.set(node.id, {
        id: node.id,
        typeName: isRuptureSet ? "Rupture Set" : node.typename,
        depth: node.depth,
        predecessors:
          node.node?.predecessors
            ?.filter((predecessor) => node.depth == (predecessor?.depth ?? 0) + 1)
            .map((predecessor) => predecessor?.id) ?? [],
      });
    }
    return acc;
  }, new Map());

  return nodes;
};

/**
 * Traverses the inheritance tree and writes out nodes in depth-first order.
 * @param data the query result
 * @returns nodes in deph-first order and their depth.
 */
const processData = (data: PredecessorsTabQuery$data) => {
  if (!data.node?.predecessors) {
    return [];
  }

  const tree = indexTree(data);

  const result: PredecessorNode[] = [];
  const seen = new Set();

  // recursive function that does a depth-first traversal of the predecessor tree and appends nodes to the `result` array.
  const visit = (predecessor: PredecessorNode, depth: number) => {
    if (predecessor.depth !== depth) {
      throw new Error("depth mismatch");
    }
    if (!seen.has(predecessor.id)) {
      result.push(predecessor);
      seen.add(predecessor.id);
    }
    predecessor.predecessors.forEach((p) => {
      const next = tree.get(p);
      if (next) {
        visit(next, depth - 1);
      }
    });
  };

  tree.forEach((node) => {
    // only consider parents of the original node
    if (node.depth === -1) {
      visit(node, -1);
    }
  });

  return result;
};

const PredecessorsTab: React.FC<PredecessorsTabProps> = ({ queryRef }: PredecessorsTabProps) => {
  const data = usePreloadedQuery<PredecessorsTabQuery>(
    predecessorsTabQuery,
    queryRef,
    // // we need to use this policy to force a network query as otherwise Predecsssor objects with
    // // wrong depth values might be used from previous queries with different id argument
    // { fetchPolicy: "store-and-network" },
  );

  if (!(data.node?.predecessors?.length && data.node?.predecessors?.length > 0)) {
    return (
      <Container>
        <Typography>No predecessors found</Typography>
      </Container>
    );
  }

  const rows = processData(data);
  const minDepth = Math.min(...rows.map((r) => r.depth));
  const renderers = [];
  for (let i = -1; i >= minDepth; i--) {
    renderers.push((cellData: object) => {
      const { depth } = cellData as { depth: number };
      if (depth == i) {
        return cellRenderer(cellData);
      }
      return null;
    });
  }

  const arrows = [];

  for (const row of rows) {
    for (const parent of row.predecessors) {
      arrows.push(
        // This is an abandoned library. In the future, we might want to replace it.
        <Xarrow
          start={row.id}
          end={parent}
          curveness={0.6}
          startAnchor="right"
          endAnchor="left"
          strokeWidth={3}
          headSize={4}
        />,
      );
    }
  }

  return (
    <Container>
      <MultiColumnTable title="Predecessors" renderers={renderers} rows={rows} styles={[]} />
      {...arrows}
    </Container>
  );
};

export default PredecessorsTab;

export const predecessorsTabQuery = graphql`
  query PredecessorsTabQuery($id: ID!) {
    node(id: $id) {
      ... on PredecessorsInterface {
        predecessors {
          id
          typename
          relationship
          depth
          node {
            ... on PredecessorsInterface {
              __typename
              predecessors {
                id
                depth
              }
            }
            ... on File {
              meta {
                k
                v
              }
            }
          }
        }
      }
    }
  }
`;
